import React, { useState, useEffect } from 'react';
import { useAuth } from '../lib/auth';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Users, UserPlus, Shield, LogOut, Copy } from 'lucide-react';
import { toast } from 'sonner';

export default function SettingsPage() {
  const { user, isAdmin, authedFetch, logout } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteRole, setInviteRole] = useState('editor');
  const [lastInvite, setLastInvite] = useState(null);

  const fetchUsers = async () => {
    try {
      const data = await authedFetch('/api/users');
      setUsers(data);
    } catch (e) {
      if (e.message.includes('403')) {
        toast.error('Admin access required');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) fetchUsers();
    else setLoading(false);
  }, [isAdmin]);

  const handleInvite = async (e) => {
    e.preventDefault();
    try {
      const data = await authedFetch('/api/users/invite', {
        method: 'POST',
        body: JSON.stringify({ email: inviteEmail, display_name: inviteName, role: inviteRole }),
      });
      setLastInvite(data);
      setInviteEmail('');
      setInviteName('');
      fetchUsers();
      toast.success('User invited');
    } catch (e) {
      toast.error(e.message || 'Failed to invite');
    }
  };

  const handleRoleChange = async (userId, role) => {
    try {
      await authedFetch(`/api/users/${userId}/role`, {
        method: 'PUT',
        body: JSON.stringify({ role }),
      });
      fetchUsers();
      toast.success('Role updated');
    } catch (e) {
      toast.error(e.message || 'Failed to update role');
    }
  };

  const handleToggleActive = async (userId, isActive) => {
    try {
      await authedFetch(`/api/users/${userId}/${isActive ? 'deactivate' : 'activate'}`, {
        method: 'PUT',
      });
      fetchUsers();
      toast.success(isActive ? 'User deactivated' : 'User activated');
    } catch (e) {
      toast.error(e.message || 'Failed');
    }
  };

  const copyToClipboard = (text) => {
    try {
      navigator.clipboard.writeText(text);
      toast.success('Copied');
    } catch {
      // Fallback
      const el = document.createElement('textarea');
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      toast.success('Copied');
    }
  };

  const ROLE_COLORS = {
    admin: 'bg-purple-100 text-purple-700',
    editor: 'bg-blue-100 text-blue-700',
    viewer: 'bg-gray-100 text-gray-700',
  };

  return (
    <div className="px-6 py-8 max-w-3xl">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Settings</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Signed in as {user?.displayName} ({user?.role})
          </p>
        </div>
        <Button variant="outline" onClick={logout} className="gap-1.5" data-testid="logout-button">
          <LogOut className="h-3.5 w-3.5" />
          Sign Out
        </Button>
      </div>

      {!isAdmin ? (
        <Card>
          <CardContent className="py-8 text-center">
            <Shield className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">Only admins can manage users.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {/* Invite */}
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-lg font-semibold flex items-center gap-2">
              <Users className="h-5 w-5" />
              Team Members ({users.length})
            </h2>
            <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
              <DialogTrigger asChild>
                <Button size="sm" className="gap-1.5" data-testid="invite-user-button">
                  <UserPlus className="h-3.5 w-3.5" />
                  Invite User
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>Invite Team Member</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleInvite} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Name</Label>
                    <Input value={inviteName} onChange={(e) => setInviteName(e.target.value)} placeholder="Jane Doe" required />
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} type="email" placeholder="jane@example.com" required />
                  </div>
                  <div className="space-y-2">
                    <Label>Role</Label>
                    <Select value={inviteRole} onValueChange={setInviteRole}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="editor">Editor</SelectItem>
                        <SelectItem value="viewer">Viewer</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button type="submit" className="w-full">Send Invite</Button>
                </form>

                {lastInvite && (
                  <div className="mt-4 rounded-lg border bg-muted/40 p-3 space-y-2">
                    <p className="text-xs font-medium">Share these credentials with the user:</p>
                    <div className="text-xs font-mono bg-background rounded p-2 space-y-1">
                      <div>Email: {lastInvite.user?.email}</div>
                      <div>Temp Password: {lastInvite.tempPassword}</div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="gap-1"
                      onClick={() => copyToClipboard(`Email: ${lastInvite.user?.email}\nPassword: ${lastInvite.tempPassword}`)}
                    >
                      <Copy className="h-3 w-3" />
                      Copy
                    </Button>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </div>

          {/* User list */}
          <div className="space-y-2">
            {users.map((u) => (
              <Card key={u.id} className={!u.isActive ? 'opacity-50' : ''}>
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center text-xs font-medium">
                      {u.displayName?.charAt(0)?.toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-sm truncate">{u.displayName}</p>
                      <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {u.id === user.id ? (
                      <Badge className={ROLE_COLORS[u.role]}>{u.role} (you)</Badge>
                    ) : (
                      <>
                        <Select value={u.role} onValueChange={(r) => handleRoleChange(u.id, r)}>
                          <SelectTrigger className="h-7 w-24 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="admin">Admin</SelectItem>
                            <SelectItem value="editor">Editor</SelectItem>
                            <SelectItem value="viewer">Viewer</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-xs"
                          onClick={() => handleToggleActive(u.id, u.isActive)}
                        >
                          {u.isActive ? 'Deactivate' : 'Activate'}
                        </Button>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
